import { fetchAuthSession } from 'aws-amplify/auth';
import { S3Client, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import { graphqlClient } from '../../services/graphqlClient';
import { localS3Overrides } from '../../services/localS3';
import type {
  Document,
  File as FileType,
  Loan,
  UploadCredentials,
} from '../../graphql/types';
import type { DocumentFieldGroup, FieldEdits } from './extractModel';
import { buildFinalizePayload } from './extractModel';

/**
 * Data calls for Extract mode, lifted from `pages/FileReview.tsx` unchanged in
 * behaviour: the PDF comes from S3 through a presigned URL (job upload
 * credentials first, identity pool as the fallback), and the reviewed fields
 * are saved with `finalizeDocumentReview`.
 */

type S3Credentials = { accessKeyId: string; secretAccessKey: string; sessionToken?: string };

async function fetchPdf(
  creds: S3Credentials,
  bucket: string,
  key: string
): Promise<ArrayBuffer> {
  const region = import.meta.env.VITE_AWS_REGION || 'us-west-2';
  const s3 = new S3Client({ region, credentials: creds, ...localS3Overrides() });
  const url = await getSignedUrl(s3, new GetObjectCommand({ Bucket: bucket, Key: key }), {
    expiresIn: 3600,
  });
  const resp = await fetch(url);
  if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
  return resp.arrayBuffer();
}

async function identityPoolCredentials(): Promise<S3Credentials> {
  const session = await fetchAuthSession();
  const c = session.credentials;
  if (!c) throw new Error('No credentials available from identity pool');
  return {
    accessKeyId: c.accessKeyId,
    secretAccessKey: c.secretAccessKey,
    sessionToken: c.sessionToken,
  };
}

export async function loadFilePdf(
  file: Pick<FileType, 's3Key'>,
  uploadBucket: string | undefined,
  uploadCredentials: UploadCredentials | null | undefined
): Promise<ArrayBuffer> {
  const bucket = uploadBucket || import.meta.env.VITE_S3_DOCUMENTS_BUCKET;
  if (uploadCredentials) {
    try {
      return await fetchPdf(
        {
          accessKeyId: uploadCredentials.accessKeyId,
          secretAccessKey: uploadCredentials.secretAccessKey,
          sessionToken: uploadCredentials.sessionToken,
        },
        bucket,
        file.s3Key
      );
    } catch (err) {
      // Job credentials are short lived: fall back to the identity pool.
      console.warn('[ExtractMode] upload credentials failed, using identity pool:', err);
    }
  }
  return fetchPdf(await identityPoolCredentials(), bucket, file.s3Key);
}

export interface ExtractData {
  loans: Loan[];
  files: FileType[];
  documents: Document[];
}

async function collectPages<T>(
  fetchPage: (token?: string) => Promise<{ items: T[]; nextToken?: string | null }>
): Promise<T[]> {
  const items: T[] = [];
  let token: string | undefined;
  do {
    const page = await fetchPage(token);
    items.push(...page.items);
    token = page.nextToken ?? undefined;
  } while (token);
  return items;
}

/**
 * Loans, files and documents of the job for Extract mode, following
 * pagination. Same queries as the legacy verification step and Analyze
 * (`listLoansByJob`, `listFilesByJob`, `listDocumentsByJob`); each document
 * carries its `extractedFields`.
 */
export async function loadExtractData(jobId: string): Promise<ExtractData> {
  const session = await fetchAuthSession();
  const tenantId =
    (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
  const queries = graphqlClient.queries;
  const [loans, files, documents] = await Promise.all([
    collectPages((t) => queries.listLoansByJob(jobId, tenantId, undefined, t)),
    collectPages((t) => queries.listFilesByJob(jobId, tenantId, undefined, t)),
    collectPages((t) => queries.listDocumentsByJob(jobId, tenantId, undefined, t)),
  ]);
  return { loans, files, documents };
}

export async function saveFileReview(
  file: Pick<FileType, 'fileId' | 'loanId' | 'jobId'>,
  groups: ReadonlyArray<DocumentFieldGroup>,
  edits: FieldEdits = {}
): Promise<void> {
  const session = await fetchAuthSession();
  const tenantId =
    (session.tokens?.idToken?.payload['custom:tenantId'] as string) || 'default-tenant';
  await graphqlClient.mutations.finalizeDocumentReview(
    file.fileId,
    file.loanId,
    file.jobId,
    tenantId,
    JSON.stringify(buildFinalizePayload(groups, edits))
  );
}
